import { Edit } from '@mui/icons-material'
import { Box, Stack, Typography, styled } from '@mui/material'
import type { KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { GridActionButton } from 'src/components/common/GridActionButton'
import type { BillingCustomer } from 'src/generated-types/customer-license'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { billingCustomerSummaryPeriodCost, formatBillingCurrency } from '../licenseContainer.action'

type CustomerBillingCardProps = {
  customer: BillingCustomer
  billingPeriodFrom: Date | null
  billingPeriodTo: Date | null
  selected: boolean
  onSelect: () => void
  onEdit: () => void
}

export const CustomerBillingCard = ({
  customer,
  billingPeriodFrom,
  billingPeriodTo,
  selected,
  onSelect,
  onEdit,
}: CustomerBillingCardProps) => {
  const { t } = useTranslation('admin-license')

  const onKeyActivate = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect()
    }
  }

  return (
    <StyledBox
      id={`customer-${customer.id}`}
      component="div"
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={String(t('selectCustomerCardAria', { name: customer.name }))}
      onClick={onSelect}
      onKeyDown={onKeyActivate}
      selected={selected}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box display="flex" flexDirection="column" sx={{ pr: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ pr: 1.5 }}>
            {customer.name}
          </Typography>
          <Typography variant="subtitle2" fontWeight={600}>
            {formatBillingCurrency(billingCustomerSummaryPeriodCost(customer, billingPeriodFrom, billingPeriodTo))}
          </Typography>
        </Box>
        <GridActionButton
          type="button"
          aria-label={String(t('editCustomerAria'))}
          toolTipTitle={String(t('editCustomerAria'))}
          onClick={e => {
            e.stopPropagation()
            onEdit()
          }}
          width="25px"
          height="25px"
          border={1}
          shadowWeight={null}
          buttonIcon={<Edit fontSize="small" />}
          sx={{ minWidth: 0, p: 0 }}
        />
      </Stack>
    </StyledBox>
  )
}

const StyledBox = styled(Box)<{
  selected: boolean
}>(({ theme, selected }) => ({
  padding: theme.spacing(0.75),
  borderRadius: '4px',
  border: '1px solid',
  borderColor: selected
    ? getThemeColor(theme, MUIThemeColors.infoMain)
    : getThemeColor(theme, MUIThemeColors.secondaryLight),
  backgroundColor: selected
    ? getThemeColor(theme, MUIThemeColors.paleBlue)
    : getThemeColor(theme, MUIThemeColors.white),
  cursor: 'pointer',
  scrollMarginTop: theme.spacing(1),
  transition: 'border-color 0.15s, background 0.15s',
  outline: 'none',
  '&:focus-visible': {
    boxShadow: `0 0 0 2px ${getThemeColor(theme, MUIThemeColors.infoMain)}`,
  },
}))
